// src/components/practice/tasks/TextReconstruction.jsx
import React, { useState } from 'react';
import { marked } from 'marked';
import { FlagButton } from '../PartDisplay';
import { X } from 'lucide-react';

const TextReconstruction = ({
  partData,
  questions,
  examples,
  partAnswers,
  handleSingleAnswer,
  flaggedQuestions,
  toggleFlagQuestion,
  images = [],
  isMobileAnswerSheetOpen,
  onCloseMobileAnswerSheet,
  onOpenMobileAnswerSheet,
  ...props
}) => {
  const allProps = { flaggedQuestions, toggleFlagQuestion, ...props };
  const { content } = partData;
  const options = content?.options || []; // This is the FULL list, including the example
  const mainText = content?.mainText || '';
const isTelcC1Part1 = partData?.examProvider?.toUpperCase() === 'TELC' &&
                      partData?.level?.toUpperCase() === 'C1' &&
                      partData?.partNumber === 1;

let title = '';
let subtitle = '';
let textBody = mainText;

if (isTelcC1Part1 || (images && images.length > 0)) {
  const lines = mainText.split('\n').filter(line => line.trim());

  if (lines.length > 0) {
    // First line is always the title (with or without **)
    title = lines[0].replace(/\*\*/g, '').trim();

    // Check if line 1 is a subtitle or body text
    if (lines[1]) {
      const line1 = lines[1].trim();
      const isSubtitle = line1.length > 0 &&
                        line1.length < 100 &&
                        !line1.includes('[...') &&
                        !line1.includes('[10') &&
                        !line1.includes('[11') &&
                        !line1.includes('[0]');

      if (isSubtitle) {
        subtitle = line1;
        textBody = lines.slice(2).join('\n');
      } else {
        subtitle = '';
        textBody = lines.slice(1).join('\n');
      }
    }
  }
}

  // --- Derive example if not provided ---
  let derivedExamples = [...examples]; // Use passed-in examples by default
  const exampleOption = options.find(o => o.isExample);

  if (examples.length === 0 && exampleOption) {
    // Manually create an example object if one wasn't in the questions array
    derivedExamples = [
      {
        questionId: `example-${exampleOption.id}`,
        blankNumber: exampleOption.id, // Assumes example option ID matches blank number
        correctAnswer: exampleOption.id,
        isExample: true
      }
    ];
  }
  // --- END ---

  const placeholderRegex = /(\[\.{3}\d+\.{3}\]|\[\d+\])/g;

  // Use the (potentially derived) example
  const exampleQuestion = derivedExamples?.[0];
  const examplePlaceholder = exampleQuestion ? `[...${exampleQuestion.blankNumber}...]` : `[...0...]`;

  // --- Get example answers from derivedExamples ---
  const exampleAnswers = derivedExamples.map(ex => ex.correctAnswer);

  const blankQuestions = questions.filter(q => !q.isExample)
    .map(q => ({
      questionId: q.questionId,
      number: q.blankNumber,
      text: `Lücke ${q.blankNumber}`,
    }));

  // 'sentenceOptions' filters out the example option
  const sentenceOptions = options.filter(o => !o.isExample)
    .map(o => ({ id: o.id, text: o.text }));

  // --- FIX ---
  // const specialOption = { id: partData.examProvider === 'TELC' ? 'x' : '0' }; // <-- REMOVED, this was the bug
  const allOptions = sentenceOptions; // <-- FIX: allOptions is just the sentenceOptions
  // --- END FIX ---


  const [selectedQuestionId, setSelectedQuestionId] = useState(null);

  const handleQuestionSelect = (questionId) => {
    setSelectedQuestionId(prevId => (prevId === questionId ? null : questionId));
  };

  // Logic allows 1-to-many (same answer for different questions)
  const handleOptionSelect = (questionId, optionId) => {
    if (partAnswers[questionId] === optionId) {
      handleSingleAnswer(questionId, null);
    } else {
      handleSingleAnswer(questionId, optionId);
    }
    setSelectedQuestionId(null);
  };

  const isOptionUsed = (optionId) => {
    // specialOption.id is removed, but we can just check against known special IDs
    if (optionId === 'x' || optionId === '0') return false;
    return Object.values(partAnswers).includes(optionId);
  };


// --- LEFT COLUMN CONTENT (Reading) ---
// --- LEFT COLUMN CONTENT (Reading) ---
const leftContent = (
  <div className="space-y-6">
    <div className="border-2 border-gray-900 bg-white rounded-none">
      {/* Title Section - For TELC C1 Part 1 OR when images exist */}
      {title && (isTelcC1Part1 || (images && images.length > 0)) && (
        <div className="py-6 px-8 bg-gray-50 border-b-2 border-gray-300">
          <h2 className="text-3xl font-bold text-gray-900 mb-0 text-center">
            {title}
          </h2>
          {subtitle && (
            <p className="text-base text-gray-700 mt-3 text-center">
              {subtitle}
            </p>
          )}
        </div>
      )}

      {/* Image - only when images exist */}
      {images && images.length > 0 && (
        <div className="w-full flex justify-center bg-gray-50">
          {images.map((imagePath, index) => (
            <img
              key={index}
              src={imagePath}
              alt={`Reading context ${index + 1}`}
              className="h-auto object-contain rounded-none"
              style={{ maxHeight: '400px', maxWidth: '100%' }}
            />
          ))}
        </div>
      )}

      {/* Main Text */}
      <div className="p-6">
        <div className="prose prose-sm max-w-none text-gray-900 leading-relaxed">
          {textBody.split(placeholderRegex).map((part, index) => {
            if (placeholderRegex.test(part)) {
              const blankNumber = part.match(/\d+/)[0];

              // Show example ID
              if (part === examplePlaceholder || (part === `[${blankNumber}]` && (blankNumber === '0' || blankNumber === exampleQuestion?.blankNumber))) {
                const exampleSentence = options.find(o => o.isExample);
                return (
                  <span key={`blank-${index}`}
                        className="inline-block text-center font-bold mx-1 px-3 py-1 bg-gray-100 border-2 border-gray-400 rounded-none">
                    ({exampleSentence?.id || '0'})
                  </span>
                );
              }

              return (
                <span key={`blank-${index}`} className="inline-block text-center font-bold mx-1 px-3 py-1 bg-gray-100 border-2 border-gray-900 rounded-none">
                  [{blankNumber}]
                </span>
              );
            }

            return <span key={`text-${index}`} dangerouslySetInnerHTML={{ __html: marked.parseInline(part, { breaks: true }) }} />;
          })}
        </div>
      </div>
    </div>
  </div>
);

  // --- RIGHT COLUMN CONTENT (Answering) ---
  const rightContent = (
    <div className="space-y-4">
      {/* Answer Sheet Header (Unchanged) */}
      <div className="border-2 border-gray-900 bg-white p-3 rounded-none">
        <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wide">
          Antwortbogen
        </h3>
        <p className="text-xs text-gray-600 mt-1">
          Welcher Satz passt in welche Lücke?
        </p>
      </div>

      {/* Answer Bank (Sentences) */}
      <div className="border-2 border-gray-900 bg-white p-4 rounded-none">
        <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">
          Sätze zum Einsetzen
        </h4>

        <ul className="list-none space-y-1">
          {/* Map over FULL 'options' list */}
          {options.map(opt => {
             const isUsedByAnswer = isOptionUsed(opt.id);
             const isUsedByExample = exampleAnswers.includes(opt.id);

             return (
              <li key={opt.id} className="pr-2">
                <span className="font-bold mr-2">{opt.id.toUpperCase()})</span>
                <span className={`text-sm transition-all ${
                  isUsedByExample 
                    ? 'text-gray-400 line-through' 
                    : isUsedByAnswer 
                      ? 'text-gray-400'
                      : 'text-gray-700'
                }`}>
                  {opt.text}
                </span>
              </li>
             );
          })}
        </ul>
      </div>

      {/* Example box (Unchanged position) */}
      <div className="border-2 border-gray-900 bg-white rounded-none">
        {derivedExamples.map((ex, idx) => {
          const exampleSentence = options.find(o => o.id === ex.correctAnswer);
          const exampleText = `Lücke ${ex.blankNumber || '0'}`;
          const correctAdText = ex.correctAnswer ? ex.correctAnswer.toUpperCase() : '?';

          return (
            <div
              key={`ex-${idx}`}
              className="bg-yellow-50 p-4 opacity-90 border-b border-gray-300 last:border-b-0"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 flex items-start">
                  <span className="font-bold text-sm mr-2 pt-0.5">
                    {ex.blankNumber || '0'}.
                  </span>
                  <span className="text-sm leading-relaxed">
                    {exampleText}
                  </span>
                </div>
                <div className="ml-4 flex-shrink-0">
                  <div
                    className="border-2 border-gray-900 bg-gray-900 text-white font-bold text-sm px-3 py-1.5 rounded-none cursor-not-allowed"
                    style={{ minWidth: '70px', textAlign: 'center' }}
                  >
                    {correctAdText}
                  </div>
                </div>
              </div>
              {exampleSentence && (
                 <div className="pl-6 mt-2 text-sm text-gray-700 italic">
                   "{exampleSentence.text}"
                 </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Matching Question list (for Blanks) */}
      <div className="border-2 border-gray-900 bg-white p-4 rounded-none space-y-3">
        {blankQuestions.map((q) => {
          const questionId = q.questionId;
          const currentAnswer = partAnswers[questionId];
          const selectedOption = allOptions.find(h => h.id === currentAnswer);
          const isSelected = selectedQuestionId === questionId;

          return (
            <div
              key={questionId}
              className={`
                bg-white rounded-none border-2 transition-all duration-200
                ${isSelected
                  ? 'border-gray-900 shadow-lg'
                  : currentAnswer
                    ? 'border-gray-900 bg-gray-50'
                    : 'border-gray-400 hover:border-gray-900'
                }
              `}
            >
              {/* Clickable Info Bar (Unchanged) */}
              <div
                onClick={() => handleQuestionSelect(questionId)}
                className={`
                  p-4 flex items-center justify-between min-h-[72px] cursor-pointer transition-all
                  ${isSelected 
                    ? 'bg-gray-900 text-white' 
                    : currentAnswer 
                      ? '' 
                      : 'hover:bg-gray-50 hover:shadow-md hover:-translate-y-0.5'
                  }
                `}
              >
                <div className="flex items-start flex-1">
                  <FlagButton {...allProps} questionId={questionId} className={isSelected ? 'flag-light' : ''} />
                  <p className={`font-semibold ml-2 ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                    <span className={`font-bold mr-2 ${isSelected ? 'text-gray-300' : 'text-gray-500'}`}>
                      {q.number}.
                    </span>
                    {q.text}
                    </p>
                </div>
                <div className="text-right ml-4">
                  {selectedOption ? (
                    <span className="font-bold text-2xl text-blue-700">{selectedOption.id.toUpperCase()}</span>
                  ) : isSelected ? (
                    <div className="w-10 h-10 bg-white rounded-md flex items-center justify-center">
                      <span className="text-gray-900 font-bold text-xl">?</span>
                    </div>
                  ) : (
                    <div className="w-10 h-10 bg-gray-200 rounded-md"></div>
                  )}
                </div>
              </div>

              {/* Inline Options Grid (appears on click) */}
              {isSelected && (
                <div className="p-4 border-t-2 border-gray-900 bg-gray-50">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-700 mb-3">
                    Satz wählen:
                  </p>
                  <div className="grid grid-cols-4 gap-2">
                    {/* This now maps over the corrected 'allOptions' (a-h)
                      and will NOT show the '0' option.
                    */}
                    {allOptions.map(option => {
                      const isCurrentAnswer = partAnswers[questionId] === option.id;
                      return (
                        <button
                          key={option.id}
                          onClick={() => handleOptionSelect(questionId, option.id)}
                          className={`
                            p-4 h-16 flex items-center justify-center rounded-none border-2 font-bold text-xl transition-all
                            ${isCurrentAnswer
                              ? 'bg-gray-900 border-gray-900 text-white shadow-lg'
                              : 'bg-white border-gray-400 text-gray-900 hover:border-gray-900 hover:bg-gray-900 hover:text-white hover:shadow-md hover:-translate-y-0.5'
                            }
                          `}
                        >
                          {option.id.toUpperCase()}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const mobileOverlay = (isMobileAnswerSheetOpen && onCloseMobileAnswerSheet) ? (
    <div className="fixed inset-0 z-[60] flex flex-col justify-end md:hidden" aria-modal="true" role="dialog" aria-label="Antwortbogen">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCloseMobileAnswerSheet} aria-hidden />
      <div className="relative bg-white rounded-t-2xl shadow-2xl max-h-[90vh] flex flex-col animate-slide-up">
        <div className="flex items-center justify-between flex-shrink-0 p-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl">
          <h3 className="text-lg font-bold text-gray-900">Antwortbogen</h3>
          <button type="button" onClick={onCloseMobileAnswerSheet} className="p-2 -m-2 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation min-w-[48px] min-h-[48px] flex items-center justify-center" aria-label="Schließen">
            <X className="w-6 h-6 text-gray-700" />
          </button>
        </div>
        <div className="overflow-y-auto overscroll-contain p-4 pb-safe">{rightContent}</div>
      </div>
    </div>
  ) : null;

  return (
    <div className="w-full max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-6">
      <div className="grid grid-cols-1 md:grid-cols-5 md:gap-8">

        {/* Left Column (Text) */}
        <div className="md:col-span-3 min-w-0">
          {leftContent}
          {onOpenMobileAnswerSheet && (
            <button
              type="button"
              onClick={onOpenMobileAnswerSheet}
              className="md:hidden w-full mt-4 py-3 px-4 bg-[#006D77] text-white font-semibold rounded-xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform touch-manipulation"
            >
              <span>Fragen beantworten</span>
              <span aria-hidden>→</span>
            </button>
          )}
        </div>

        {/* Right Column (Answer Sheet) - hidden on mobile */}
        <div className="hidden md:block md:col-span-2 mt-6 md:mt-0">
          <div className="md:sticky md:top-6">
            {rightContent}
          </div>
        </div>

      </div>
      {mobileOverlay}
    </div>
  );
};

export default TextReconstruction;
